/**
 * What makes a dashboard row a *layout*.
 *
 * A layout is a dashboard by storage — same table, same editors, same live
 * bindings — flagged in its own `layoutConfig` with the marker the hub writes
 * (`otto/src/layouts/builtinLayouts.ts`). The flag is what keeps a layout out of
 * the dashboard list and lets a category page find its hero.
 *
 * The hub names this shape once; this module is the UI's read of it. Keep the
 * two in step: the test beside this file pins the fields both sides rely on.
 */
import { isWireObject } from './wire';

export const LAYOUT_ROLE = 'layout';

/**
 * What a layout is drawn on (`t_layouts`, 2026-10-27 · merged 2026-10-28).
 *
 * TWO bases, not three. A floor plan and a process diagram were never different
 * things: both are a canvas you draw devices on, and only the picture underneath
 * differed. So `canvas` is the one 2D base and `model` is the hub's 3D model —
 * at most one layout has it. A layout is a *floor plan* when it carries a plan
 * picture (`planId`), and a *process diagram* when it does not.
 */
export type LayoutBaseKind = 'canvas' | 'model';

export interface LayoutBase {
  kind: LayoutBaseKind;
  /** The hub plan painted behind a `canvas` base, when it has one. */
  planId?: string;
}

export const LAYOUT_BASE_KINDS: LayoutBaseKind[] = ['canvas', 'model'];

/**
 * Every spelling the two merged kinds used to have. A stored row keeps the base
 * it was written with until its next save, so both the marker's own words and
 * the retired `layoutType` values have to answer.
 */
const CANVAS_ALIASES = ['plan', 'process'] as const;

function canvasBaseOf(kind: unknown, planId: unknown): LayoutBase {
  const isCanvas = kind === 'canvas' || (CANVAS_ALIASES as readonly unknown[]).includes(kind);
  if (!isCanvas && kind !== 'model') return { kind: 'canvas' };
  const id = typeof planId === 'string' && planId !== '' ? planId : undefined;
  if (kind === 'model') return { kind: 'model' };
  return id ? { kind: 'canvas', planId: id } : { kind: 'canvas' };
}

/** The base a row is drawn on, read from the marker or derived from its shape. */
export function layoutBaseOf(layoutConfig: unknown, layoutType?: string | null): LayoutBase {
  const base = readLayoutMarker(layoutConfig)?.base;
  if (base && (base.kind !== undefined || base.planId !== undefined)) return canvasBaseOf(base.kind, base.planId);
  // A row written before the base existed: its wireframe says what it is. A
  // floor plan's picture is the row's `basePlanId`, which the caller reads
  // separately, so a legacy plan answers `canvas` with no id here.
  if (layoutType === 'bim') return { kind: 'model' };
  return { kind: 'canvas' };
}

/** Which category pages show the layout as a hero, and in what order. */
export interface LayoutHeroConfig {
  categories: string[];
  /** Slide order within a category; lower first. */
  order: number;
  /** Frame height cap in CSS pixels. */
  maxHeight?: number;
}

export interface LayoutMarker {
  role: typeof LAYOUT_ROLE;
  /** The document shape. `1`/absent is a legacy page; `2` is a layout. */
  version?: number;
  /** What the layout is drawn on (`version: 2`). */
  base?: LayoutBase;
  /** Set on the rows the hub ships (`water`, …). */
  builtinKey?: string;
  hero?: LayoutHeroConfig;
}

/** The `layoutConfig.layout` marker, or undefined when the row is a dashboard. */
export function readLayoutMarker(layoutConfig: unknown): LayoutMarker | undefined {
  if (!isWireObject(layoutConfig)) return undefined;
  const marker = (layoutConfig as Record<string, unknown>).layout;
  if (!isWireObject(marker)) return undefined;
  if ((marker as Record<string, unknown>).role !== LAYOUT_ROLE) return undefined;
  return marker as unknown as LayoutMarker;
}

/** Is this row a layout rather than a dashboard? */
export function isLayoutDashboard(
  row: { layoutConfig?: unknown } | null | undefined,
): boolean {
  return readLayoutMarker(row?.layoutConfig) !== undefined;
}

/** The one `layoutType` a canvas carries from now on (a base says what it draws). */
export const LAYOUT_TYPE = 'layout';

/**
 * The canvas a row draws, or `undefined` when the row is a page of cards.
 *
 * ONE type, TWO bases: a floor plan and a process diagram are the same kind of
 * thing — a canvas — and only what sits underneath differs (a plan picture, or
 * nothing). The retired `floorplan` / `scada` types still answer, so a hub that
 * has not been migrated keeps opening.
 */
export function layoutCanvasOf(
  row: { layoutConfig?: unknown; layoutType?: string | null } | null | undefined,
): LayoutBaseKind | undefined {
  if (!row) return undefined;
  if (readLayoutMarker(row.layoutConfig)) return layoutBaseOf(row.layoutConfig, row.layoutType).kind;
  if (row.layoutType === 'floorplan' || row.layoutType === 'scada' || row.layoutType === 'bim') {
    return layoutBaseOf(undefined, row.layoutType).kind;
  }
  return undefined;
}

/** Is this row drawn on a canvas at all? (Any base, new or legacy type.) */
export function isCanvasLayout(
  row: { layoutConfig?: unknown; layoutType?: string | null } | null | undefined,
): boolean {
  return layoutCanvasOf(row) !== undefined;
}

/** The categories a layout is a hero for (empty when it is a plain layout). */
export function layoutHeroCategories(layoutConfig: unknown): string[] {
  const categories = readLayoutMarker(layoutConfig)?.hero?.categories;
  return Array.isArray(categories)
    ? categories.filter((category): category is string => typeof category === 'string')
    : [];
}

/** The layout's slide order within a category (0 when it says nothing). */
export function layoutHeroOrder(layoutConfig: unknown): number {
  const order = readLayoutMarker(layoutConfig)?.hero?.order;
  return typeof order === 'number' && Number.isFinite(order) ? order : 0;
}

/** The layout's frame height cap, when it sets one. */
export function layoutHeroMaxHeight(layoutConfig: unknown): number | undefined {
  const maxHeight = readLayoutMarker(layoutConfig)?.hero?.maxHeight;
  return typeof maxHeight === 'number' && maxHeight > 0 ? maxHeight : undefined;
}

/**
 * The automation pages a layout can be a hero for, in nav order. The slug is what
 * the page mounts (`views/Water.vue` → `category="water"`); the label is the
 * owner's word for that page.
 */
export const LAYOUT_HERO_CATEGORIES: ReadonlyArray<{ slug: string; label: string }> = [
  { slug: 'water', label: 'Water' },
  { slug: 'lighting', label: 'Lights' },
  { slug: 'shading', label: 'Shades' },
  { slug: 'climate', label: 'Climate' },
  { slug: 'security', label: 'Security' },
  { slug: 'energy', label: 'Energy' },
  { slug: 'appliances', label: 'Appliances' },
  { slug: 'wellness', label: 'Wellness' },
];

/**
 * The `layoutConfig` with this layout's hero categories replaced, or with the hero
 * removed when it is on no page.
 *
 * The marker is carried through untouched — a hub-shipped layout that is saved
 * from the editor must not lose its `builtinKey` (which is what re-ships it) or
 * its `base` (which is what the editor draws on). Returning the WHOLE config is
 * deliberate: the caller writes it back as one value, so nothing else in the bag
 * can be dropped by a partial write.
 */
export function withLayoutHero(
  layoutConfig: unknown,
  hero: { categories: readonly string[]; order?: number; maxHeight?: number } | null,
): Record<string, unknown> {
  const config: Record<string, unknown> = isWireObject(layoutConfig)
    ? { ...(layoutConfig as Record<string, unknown>) }
    : {};
  const marker = readLayoutMarker(config);
  if (!marker) return config;

  const known = new Set(LAYOUT_HERO_CATEGORIES.map((entry) => entry.slug));
  const categories = [
    ...new Set((hero?.categories ?? []).filter((slug) => known.has(slug))),
  ];

  const nextMarker: LayoutMarker = { ...marker };
  if (categories.length === 0) {
    delete nextMarker.hero;
  } else {
    nextMarker.hero = {
      ...marker.hero,
      categories,
      order:
        typeof hero?.order === 'number' && Number.isFinite(hero.order) ? hero.order : (marker.hero?.order ?? 0),
    };
    if (typeof hero?.maxHeight === 'number' && hero.maxHeight > 0) {
      nextMarker.hero.maxHeight = hero.maxHeight;
    }
  }

  config.layout = nextMarker as unknown as Record<string, unknown>;
  return config;
}
